/**
 * SafeVenuesAdminPanel — Founder/admin UI for curating the
 * `public.safe_venues` table.
 *
 * Why this exists
 * ---------------
 * A pre-meet checklist waives the Trusted Circle sharing requirement
 * only when the venue is curated, in the same city as the meet, AND has
 * documented safety steps (`safe_venues.why_safe` length >= 10). Without
 * an admin UI, the "documented safety steps" field can't be added or
 * edited, so members never get the waiver in practice. This panel lets
 * admins add and edit venues — especially the safety-steps text.
 *
 * Writes go directly through the Supabase client; access is gated by
 * the policies added in 0027_safe_venues_admin_writes.sql (admin role
 * or founder).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  SAFE_VENUE_CATEGORY_LABELS,
} from "@/lib/safety/useSafeVenues";

interface SafeVenueRow {
  id: string;
  name: string;
  category: string;
  city: string | null;
  country: string | null;
  lat: number;
  lng: number;
  why_safe: string | null;
  hours: string | null;
  url: string | null;
  active: boolean;
  updated_at: string | null;
}

const EMPTY_DRAFT: Omit<SafeVenueRow, "id" | "updated_at"> = {
  name: "",
  category: "cafe",
  city: "",
  country: "",
  lat: 0,
  lng: 0,
  why_safe: "",
  hours: "",
  url: "",
  active: true,
};

const SAFETY_STEPS_MIN = 10;

export const SafeVenuesAdminPanel = () => {
  const [rows, setRows] = useState<SafeVenueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<typeof EMPTY_DRAFT>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("safe_venues")
      .select(
        "id,name,category,city,country,lat,lng,why_safe,hours,url,active,updated_at",
      )
      .order("city", { ascending: true, nullsFirst: false })
      .order("name", { ascending: true })
      .limit(500);
    if (error) {
      toast({
        title: "Couldn't load venues",
        description: error.message,
        variant: "destructive",
      });
    }
    setRows((data ?? []) as SafeVenueRow[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.city, r.country, r.category]
        .filter(Boolean)
        .some((s) => (s as string).toLowerCase().includes(q)),
    );
  }, [rows, filter]);

  const startEdit = (row: SafeVenueRow) => {
    setEditingId(row.id);
    setDraft({
      name: row.name,
      category: row.category,
      city: row.city ?? "",
      country: row.country ?? "",
      lat: row.lat,
      lng: row.lng,
      why_safe: row.why_safe ?? "",
      hours: row.hours ?? "",
      url: row.url ?? "",
      active: row.active,
    });
  };

  const startCreate = () => {
    setEditingId("__new__");
    setDraft(EMPTY_DRAFT);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const safetyStepsLen = (draft.why_safe ?? "").trim().length;
  const safetyStepsOk = safetyStepsLen === 0 || safetyStepsLen >= SAFETY_STEPS_MIN;
  const willGrantWaiver = safetyStepsLen >= SAFETY_STEPS_MIN;

  const formValid =
    draft.name.trim().length >= 2 &&
    draft.category.trim().length > 0 &&
    Number.isFinite(draft.lat) &&
    Number.isFinite(draft.lng) &&
    Math.abs(draft.lat) <= 90 &&
    Math.abs(draft.lng) <= 180 &&
    safetyStepsOk;

  const save = async () => {
    if (!formValid || editingId === null) return;
    setBusy(true);
    const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
    const payload = {
      name: draft.name.trim(),
      category: draft.category,
      city: draft.city?.trim() || null,
      country: draft.country?.trim() || null,
      lat: Number(draft.lat),
      lng: Number(draft.lng),
      why_safe: (draft.why_safe ?? "").trim() || null,
      hours: draft.hours?.trim() || null,
      url: draft.url?.trim() || null,
      active: draft.active,
      updated_by: userId,
    };
    const op =
      editingId === "__new__"
        ? supabase.from("safe_venues").insert(payload).select().single()
        : supabase
            .from("safe_venues")
            .update(payload)
            .eq("id", editingId)
            .select()
            .single();
    const { error } = await op;
    setBusy(false);
    if (error) {
      toast({
        title: "Save failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({
      title: editingId === "__new__" ? "Venue added" : "Venue updated",
      description: willGrantWaiver
        ? "Documented safety steps recorded — members at this venue (same city) will be eligible to waive Trusted Circle sharing."
        : "Saved without documented safety steps — Trusted Circle sharing will still be required for this venue.",
    });
    cancel();
    await reload();
  };

  const toggleActive = async (row: SafeVenueRow) => {
    const { error } = await supabase
      .from("safe_venues")
      .update({ active: !row.active })
      .eq("id", row.id);
    if (error) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    await reload();
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-2">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h3 className="text-base font-medium">Curated safe venues</h3>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Members get Trusted Circle sharing waived for a pre-meet only when
              the chosen venue is{" "}
              <span className="text-foreground">curated</span>, in the{" "}
              <span className="text-foreground">same city</span> as the meet,
              and has{" "}
              <span className="text-foreground">documented safety steps</span>{" "}
              (≥ {SAFETY_STEPS_MIN} characters in the safety-steps field). Edit
              that field carefully — it's what unlocks the waiver.
            </p>
          </div>
          <Button onClick={startCreate} disabled={editingId !== null}>
            Add venue
          </Button>
        </div>
      </Card>

      {editingId !== null && (
        <Card className="p-6 space-y-4 border-primary/40">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">
              {editingId === "__new__" ? "New venue" : "Editing venue"}
            </h4>
            <Badge variant={willGrantWaiver ? "default" : "outline"}>
              {willGrantWaiver
                ? "Will grant Trusted Circle waiver (same-city)"
                : "Won't grant waiver — needs documented safety steps"}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sv-name">Name</Label>
              <Input
                id="sv-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                maxLength={120}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sv-cat">Category</Label>
              <Select
                value={draft.category}
                onValueChange={(v) => setDraft({ ...draft, category: v })}
              >
                <SelectTrigger id="sv-cat">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SAFE_VENUE_CATEGORY_LABELS).map(([k, label]) => (
                    <SelectItem key={k} value={k}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sv-city">City</Label>
              <Input
                id="sv-city"
                value={draft.city ?? ""}
                onChange={(e) => setDraft({ ...draft, city: e.target.value })}
                maxLength={80}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sv-country">Country</Label>
              <Input
                id="sv-country"
                value={draft.country ?? ""}
                onChange={(e) => setDraft({ ...draft, country: e.target.value })}
                maxLength={80}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sv-lat">Latitude</Label>
              <Input
                id="sv-lat"
                type="number"
                step="0.000001"
                value={draft.lat}
                onChange={(e) =>
                  setDraft({ ...draft, lat: Number(e.target.value) })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sv-lng">Longitude</Label>
              <Input
                id="sv-lng"
                type="number"
                step="0.000001"
                value={draft.lng}
                onChange={(e) =>
                  setDraft({ ...draft, lng: Number(e.target.value) })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sv-hours">Hours (free text)</Label>
              <Input
                id="sv-hours"
                value={draft.hours ?? ""}
                onChange={(e) => setDraft({ ...draft, hours: e.target.value })}
                maxLength={120}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sv-url">URL</Label>
              <Input
                id="sv-url"
                type="url"
                value={draft.url ?? ""}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                maxLength={300}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="sv-why">
                Documented safety steps{" "}
                <span className="text-xs text-muted-foreground">
                  (the &quot;why this venue is safe&quot; description members see)
                </span>
              </Label>
              <span
                className={`text-xs ${
                  safetyStepsOk ? "text-muted-foreground" : "text-destructive"
                }`}
              >
                {safetyStepsLen} / min {SAFETY_STEPS_MIN} chars to grant waiver
              </span>
            </div>
            <Textarea
              id="sv-why"
              rows={4}
              value={draft.why_safe ?? ""}
              onChange={(e) => setDraft({ ...draft, why_safe: e.target.value })}
              maxLength={1000}
              placeholder="e.g. Public, well-lit, staff trained on Wavlynq's safety protocol; visible camera coverage; staff will call emergency services if requested."
            />
            {!safetyStepsOk && (
              <p className="text-xs text-destructive">
                Either leave this empty or write at least {SAFETY_STEPS_MIN}{" "}
                characters — short notes aren't substantive enough to grant the
                Trusted Circle waiver.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="sv-active"
              checked={draft.active}
              onCheckedChange={(v) => setDraft({ ...draft, active: v })}
            />
            <Label htmlFor="sv-active" className="cursor-pointer">
              Active (visible to members)
            </Label>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={save} disabled={busy || !formValid}>
              {busy ? "Saving…" : editingId === "__new__" ? "Add venue" : "Save changes"}
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <h4 className="text-sm font-medium">All venues ({rows.length})</h4>
          <Input
            placeholder="Filter by name, city, country, category…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-sm"
          />
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No venues match.</p>
        ) : (
          <ul className="space-y-2">
            {filtered.map((row) => {
              const docOk = (row.why_safe ?? "").trim().length >= SAFETY_STEPS_MIN;
              return (
                <li key={row.id}>
                  <Card className="p-4 flex items-start justify-between gap-4">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{row.name}</span>
                        <Badge variant="secondary">
                          {SAFE_VENUE_CATEGORY_LABELS[row.category] ??
                            row.category}
                        </Badge>
                        {!row.active && (
                          <Badge variant="outline" className="text-muted-foreground">
                            Inactive
                          </Badge>
                        )}
                        <Badge
                          variant={docOk ? "default" : "outline"}
                          className={
                            docOk
                              ? "bg-emerald-600 hover:bg-emerald-600"
                              : "text-muted-foreground"
                          }
                        >
                          {docOk ? "Safety steps documented" : "No safety steps"}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {[row.city, row.country].filter(Boolean).join(", ") ||
                          "Unknown city"}{" "}
                        · {row.lat.toFixed(4)}, {row.lng.toFixed(4)}
                      </div>
                      {row.why_safe && (
                        <p className="text-xs text-muted-foreground line-clamp-2 max-w-2xl">
                          {row.why_safe}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEdit(row)}
                        disabled={editingId !== null}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleActive(row)}
                      >
                        {row.active ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default SafeVenuesAdminPanel;
